import { useState, useEffect, useRef } from 'react';
import {
  Box, Typography, TextField, Button, Paper,
  CircularProgress, Alert,
} from '@mui/material';
import api from '../config/api';
import { useTranslation } from 'react-i18next';

/**
 * Confirmación de identidad al postular (PLAN_MASIVO_PORTAL, fase 0).
 *
 * La ficha del proyecto con el documento de la persona tiene OTRO email
 * (POST /applications respondió IDENTITY_REQUIRED). Se envía un código a ese
 * email (se muestra enmascarado) y, al confirmarlo, la ficha queda vinculada a
 * la cuenta; después el formulario reintenta la postulación (onConfirmed).
 *
 * Nadie queda sin salida (PLAN_IMPORTACION_COMPUTRABAJO, fase 0): sin acceso a
 * ese correo, o si la ficha no se puede reclamar (es de otra cuenta, o no
 * tiene un correo utilizable), la persona postula igual (onApplyAnyway) y la
 * postulación queda "Por validar" para que un reclutador revise sus datos.
 * startBlocked: el backend ya respondió que la ficha no se puede reclamar.
 */
const PortalIdentityConfirm = ({
  positionId, sessionToken, maskedEmail, onConfirmed, onCancel, onApplyAnyway = null, startBlocked = false,
}) => {
  const { t } = useTranslation();
  const [code, setCode] = useState('');
  const [email, setEmail] = useState(maskedEmail || '');
  const [sending, setSending] = useState(!startBlocked);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState(startBlocked ? t('portal.identity.linkedElsewhereApply') : null);
  // No se puede seguir por aquí (la ficha es de otra cuenta): postula igual
  const [blocked, setBlocked] = useState(startBlocked);
  const [cooldown, setCooldown] = useState(0);
  const requested = useRef(false);

  const headers = { 'x-candidate-token': sessionToken };

  // Respuestas que no son "código incorrecto"; true si ya se resolvió
  const handleLinkError = (err) => {
    const status = err.response?.status;
    const type = err.response?.data?.response?.errorType;
    if (type === 'NOTHING_TO_LINK') {
      onConfirmed();
      return true;
    }
    if (type === 'APPLICANT_LINKED_ELSEWHERE' || type === 'APPLICANT_EMAIL_UNAVAILABLE') {
      setBlocked(true);
      setError(t(onApplyAnyway ? 'portal.identity.linkedElsewhereApply' : 'portal.identity.linkedElsewhere'));
      return true;
    }
    if (status === 429) {
      setError(t('portal.tooManyRequests'));
      setCooldown(60);
      return true;
    }
    return false;
  };

  const requestCode = async () => {
    setSending(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/public/candidates/me/link-applicant/request-otp`,
        { positionId },
        { headers }
      );
      if (data.response?.status_response === 'OK') {
        setEmail(data.response.maskedEmail || maskedEmail || '');
        setCooldown(60);
      } else {
        setError(t('portal.otpSendError'));
      }
    } catch (err) {
      console.error(err);
      if (!handleLinkError(err)) setError(t('portal.otpSendError'));
    } finally {
      setSending(false);
    }
  };

  // El código se pide una sola vez al abrir (StrictMode monta dos veces)
  useEffect(() => {
    if (requested.current || startBlocked) return;
    requested.current = true;
    requestCode();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    if (!/^\d{6}$/.test(code)) {
      setError(t('portal.otpIncomplete'));
      return;
    }
    setVerifying(true);
    setError(null);
    try {
      const { data } = await api.post(
        `/public/candidates/me/link-applicant/verify`,
        { positionId, otp: code },
        { headers }
      );
      if (data.response?.status_response === 'OK') {
        onConfirmed();
        return;
      }
      setError(t('portal.otpInvalid'));
    } catch (err) {
      console.error(err);
      if (err.response?.status === 429) {
        setError(t('portal.tooManyAttempts'));
      } else if (!handleLinkError(err)) {
        setError(t('portal.otpInvalid'));
      }
    } finally {
      setVerifying(false);
    }
  };

  return (
    <Paper elevation={0} sx={{ border: '1px solid #E5E7EB', borderRadius: 2, p: 3, mb: 3 }}>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t('portal.identity.title')}
      </Typography>
      {!blocked && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {t('portal.otpSubtitleRegistered', { email })}
        </Typography>
      )}

      {error && (
        <Alert severity={blocked ? 'warning' : 'error'} sx={{ mb: 2 }}>{error}</Alert>
      )}

      {!blocked && (
        <>
          <TextField
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            label={t('portal.identity.codeLabel')}
            fullWidth
            disabled={sending}
            inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }}
            sx={{ mb: 2 }}
          />
          <Button
            variant="contained"
            fullWidth
            onClick={handleVerify}
            disabled={sending || verifying || code.length < 6}
            startIcon={verifying ? <CircularProgress size={18} color="inherit" /> : null}
            sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5, mb: 1 }}
          >
            {verifying ? t('portal.verifying') : t('portal.identity.confirm')}
          </Button>
          <Button
            fullWidth
            size="small"
            onClick={requestCode}
            disabled={sending || cooldown > 0}
          >
            {cooldown > 0 ? t('portal.resendIn', { seconds: cooldown }) : t('portal.resend')}
          </Button>
          {onApplyAnyway ? (
            <>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
                {t('portal.identity.noAccessHint')}
              </Typography>
              <Button fullWidth size="small" onClick={onApplyAnyway} disabled={verifying} sx={{ mt: 0.5 }}>
                {t('portal.identity.noAccess')}
              </Button>
            </>
          ) : (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2, textAlign: 'center' }}>
              {t('portal.noAccessToEmail')}
            </Typography>
          )}
        </>
      )}

      {blocked && onApplyAnyway && (
        <Button
          variant="contained"
          fullWidth
          onClick={onApplyAnyway}
          sx={{ backgroundColor: '#6A5AF9', '&:hover': { backgroundColor: '#5B4DE8' }, py: 1.5 }}
        >
          {t('portal.identity.applyAnyway')}
        </Button>
      )}

      <Box sx={{ mt: 1 }}>
        <Button fullWidth size="small" color="inherit" onClick={onCancel}>
          {t('portal.back')}
        </Button>
      </Box>
    </Paper>
  );
};

export default PortalIdentityConfirm;
